import { getGatewayErrorCode } from '@/shared/api/client'

import {
  type PushSettingsResponse,
  subscribePush,
  unsubscribePush
} from '../api/notificationsApi'
import {
  encodeKey,
  getBrowserPushSubscription,
  hasApplicationServerKey,
  isWebPushSupported,
  PUSH_SW_PATH,
  urlBase64ToUint8Array
} from './pushSubscription'

/** The endpoint is registered to another account on this browser. */
const ENDPOINT_TAKEN = 'NT-200'

export type WebPushFailure = 'unsupported' | 'default' | 'denied'

export class WebPushError extends Error {
  constructor(readonly reason: WebPushFailure) {
    super(`web push: ${reason}`)
    this.name = 'WebPushError'
  }
}

const PUSH_LOCK = 'arena-web-push'
let localQueue: Promise<unknown> = Promise.resolve()

/**
 * One change of the browser's subscription at a time, across tabs where Web
 * Locks exist: a tab syncing on load must not drop the subscription another
 * tab has just created and not yet registered.
 */
export function withPushLock<T>(task: () => Promise<T>): Promise<T> {
  if (
    typeof navigator !== 'undefined' &&
    typeof navigator.locks?.request === 'function'
  ) {
    return navigator.locks.request(PUSH_LOCK, task)
  }

  const run = localQueue.then(task, task)
  localQueue = run.catch(() => undefined)
  return run
}

const sendSubscription = (subscription: PushSubscription, language: string) =>
  subscribePush({
    endpoint: subscription.endpoint,
    p256dh: encodeKey(subscription.getKey('p256dh')),
    auth: encodeKey(subscription.getKey('auth')),
    user_agent: navigator.userAgent,
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language
  })

const ensureRegistration = async () => {
  const registration =
    (await navigator.serviceWorker.getRegistration(PUSH_SW_PATH)) ??
    (await navigator.serviceWorker.register(PUSH_SW_PATH))
  await navigator.serviceWorker.ready
  return registration
}

/**
 * Subscribes this browser and registers it for the signed-in account. The
 * permission has to be granted already: asking for it belongs to a click.
 */
export async function registerThisBrowser({
  vapidPublicKey,
  language
}: {
  vapidPublicKey: string
  language: string
}): Promise<PushSubscription> {
  if (!isWebPushSupported() || !vapidPublicKey) {
    throw new WebPushError('unsupported')
  }
  if (Notification.permission !== 'granted') {
    throw new WebPushError(
      Notification.permission === 'denied' ? 'denied' : 'default'
    )
  }

  const registration = await ensureRegistration()
  const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey)
  const subscribe = () =>
    registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey
    })

  let subscription = await registration.pushManager.getSubscription()

  // Made with a rotated VAPID key: the push service rejects every message
  // signed with the new one, so reusing it would go quiet without an error.
  if (subscription && !hasApplicationServerKey(subscription, vapidPublicKey)) {
    const stale = subscription.endpoint
    await subscription.unsubscribe()
    await unsubscribePush(stale).catch(() => undefined)
    subscription = null
  }

  subscription ??= await subscribe()

  try {
    await sendSubscription(subscription, language)
  } catch (error) {
    if (getGatewayErrorCode(error) !== ENDPOINT_TAKEN) {
      throw error
    }

    // A push subscription belongs to the browser, not the account, so on a
    // shared browser this endpoint is still the previous fan's. The service
    // refuses to move it; dropping it here mints a new one.
    await subscription.unsubscribe()
    subscription = await subscribe()
    await sendSubscription(subscription, language)
  }

  return subscription
}

/**
 * Logout: the server forgets this browser, then the browser drops the
 * subscription. Must run while the session is still alive. Never throws.
 */
export async function detachThisBrowser(): Promise<void> {
  const subscription = await getBrowserPushSubscription()
  if (!subscription) {
    return
  }

  await unsubscribePush(subscription.endpoint).catch(() => undefined)
  await subscription.unsubscribe().catch(() => undefined)
}

/**
 * Accounts that connected this browser themselves. After a logout the browser
 * is detached; it is reconnected on the next login only for them, so a shared
 * browser never starts showing another account's pushes on its own.
 */
const PUSH_USERS_KEY = 'notifications:push-users'
const PUSH_USERS_LIMIT = 20

const readPushUsers = (): string[] => {
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(PUSH_USERS_KEY) ?? '[]'
    )
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : []
  } catch {
    return []
  }
}

export function rememberPushUser(userId: string) {
  const users = readPushUsers().filter(id => id !== userId)
  users.push(userId)
  try {
    window.localStorage.setItem(
      PUSH_USERS_KEY,
      JSON.stringify(users.slice(-PUSH_USERS_LIMIT))
    )
  } catch {
    // Private mode: the fan reconnects the browser by hand after a login.
  }
}

export function isRememberedPushUser(userId: string): boolean {
  return readPushUsers().includes(userId)
}

/**
 * Brings this browser's subscription in line with the signed-in account,
 * without ever asking for a permission:
 *
 * - registered for the account → refreshed after a VAPID rotation or a change
 *   of the site language (the service renders pushes in it);
 * - registered to nobody here → it is the previous account's, dropped;
 * - none, the account has push on and connected this browser before →
 *   reconnected (a logout detaches the browser).
 *
 * `pushEnabled` may be lazy: the settings are only needed to reconnect.
 * Resolves true when the subscription changed.
 */
export async function syncThisBrowser({
  userId,
  language,
  push,
  pushEnabled
}: {
  userId: string
  language: string
  push: PushSettingsResponse
  pushEnabled: boolean | (() => Promise<boolean>)
}): Promise<boolean> {
  const vapidPublicKey = push.vapid_public_key
  if (
    !isWebPushSupported() ||
    Notification.permission !== 'granted' ||
    !vapidPublicKey
  ) {
    return false
  }

  const subscription = await getBrowserPushSubscription()
  const registered = subscription
    ? push.subscriptions?.find(item => item.endpoint === subscription.endpoint)
    : undefined

  if (subscription && registered) {
    rememberPushUser(userId)
    if (
      hasApplicationServerKey(subscription, vapidPublicKey) &&
      (registered.language ?? '') === language
    ) {
      return false
    }
    await registerThisBrowser({ vapidPublicKey, language })
    return true
  }

  let changed = false
  if (subscription) {
    changed = await subscription.unsubscribe().catch(() => false)
  }

  if (!isRememberedPushUser(userId)) {
    return changed
  }

  const enabled =
    typeof pushEnabled === 'function' ? await pushEnabled() : pushEnabled
  if (!enabled) {
    return changed
  }

  await registerThisBrowser({ vapidPublicKey, language })
  return true
}

/** Logout waits for the server this long at most; the fan is leaving. */
const DETACH_TIMEOUT_MS = 3000

/** `detachThisBrowser` for the logout button: bounded, never throws. */
export async function detachBeforeLogout(): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  await Promise.race([
    withPushLock(detachThisBrowser).catch(() => undefined),
    new Promise<void>(resolve => {
      timer = setTimeout(resolve, DETACH_TIMEOUT_MS)
    })
  ])
  clearTimeout(timer)
}
